import { DeleteConfirmPopup } from '@components/DeleteConfirm';
import { brand } from '@utility/config';
import { Flag } from '@elements/Flag';
import { FormHelpToolTip, GreenRadio } from '@elements/FormField';
import { useMarketplaceSyncHook } from '@hooks/marketplace';
import { StatusBox } from '@page-components/catalog-settings/tabs/MarketplacesTab';
import { CommonUtility } from '@utility/common';
import { marketplaceNames, tooltipTexts } from '@utility/constants/common';
import { ArrowCounterClockwise, X } from '@phosphor-icons/react';
import {
  Badge,
  Button,
  Image as KitImage,
  Spinner,
  Tooltip,
  cn,
} from '@skupreme/shared/ui';
import { type ReactNode, useEffect, useMemo, useState } from 'react';
import { getTabPermission, useAuth } from '@contexts/auth';
import PermissionTooltip from '@components/PermissionTooltip';
import MarketplaceError from '@page-components/onboarding/Connecting/MarketplaceError';
import SellerCloudMetadataPopup from '@page-components/marketplace-integration/SellerCloudMetaDataPopup';
import { marketplaceWordmarkImages } from '@utility/constants/marketplace';
import { PermissionTabKey } from '@utility/constants/permission';
import MarketPlaceService from '@utility/services/marketplace';
import { ToastMessage } from '@utility/toast';
import PercentageProgress from '../Common/PercentageProgress';
import { AppTableRow, AppTableCell } from '@components/AppTable';

type MarketplaceErrorEntry = {
  resolved?: boolean;
  reason?: string;
  error_details?: string;
};

type LogoAvatarProps = {
  name?: string | null;
  className?: string;
};

/** A marketplace's first letter on a tile, for when it has no usable logo. */
export function LogoAvatar({ name, className }: LogoAvatarProps) {
  const initial = name ? name[0] : '';
  return (
    <span
      aria-hidden
      className={cn(
        'sk:ml-5 sk:inline-flex sk:size-9 sk:shrink-0 sk:items-center sk:justify-center',
        'sk:rounded-option sk:bg-fill-tertiary sk:font-semibold sk:text-fg',
        className,
      )}
    >
      {initial}
    </span>
  );
}

type SyncToggleProps = {
  label: string;
  helpText: ReactNode;
  checked: boolean;
  isDisabled?: boolean;
  onToggle: () => void;
  /** The tab permission: a disabled tab explains itself, a hidden one drops the switch. */
  disabled: boolean;
  hidden: boolean;
  className?: string;
};

/* Switch, label and help on one line: the cells are narrow and a wrapped label reads as two settings. */
function SyncToggle({
  label,
  helpText,
  checked,
  isDisabled,
  onToggle,
  disabled,
  hidden,
  className,
}: SyncToggleProps) {
  return (
    <div
      className={cn(
        'sk:flex sk:items-center sk:gap-2 sk:whitespace-nowrap sk:text-dense sk:text-fg',
        className,
      )}
    >
      <PermissionTooltip
        disabled={disabled}
        hidden={hidden}
        position="bottom center"
      >
        <GreenRadio
          toggle
          aria-label={label}
          checked={checked}
          disabled={isDisabled}
          onClick={onToggle}
        />
      </PermissionTooltip>
      <span>{label}</span>
      <FormHelpToolTip
        helpText={helpText}
        helpPosition="top center"
        leftMargin={false}
      />
    </div>
  );
}

type RowActionProps = {
  label: string;
  icon: ReactNode;
  isDisabled?: boolean;
  onPress: () => void;
};

function RowAction({ label, icon, isDisabled, onPress }: RowActionProps) {
  return (
    <Tooltip content={label} placement="top" asChild>
      <Button
        variant="ghost"
        size="sm"
        shape="circle"
        aria-label={label}
        isDisabled={isDisabled}
        onPress={onPress}
      >
        {icon}
      </Button>
    </Tooltip>
  );
}

export const isInventorySyncAllowedMarketplace = (marketplaceType = '') =>
  [
    marketplaceNames.walmart,
    marketplaceNames.etsy,
    marketplaceNames.shopify,
    marketplaceNames.eBay,
    marketplaceNames.tiktok,
    marketplaceNames['target plus'],
    marketplaceNames.wayfair,
    marketplaceNames.temu,
    marketplaceNames.sellercloud,
    marketplaceNames.netsuite,
    marketplaceNames.mirakl,
  ].includes(marketplaceType);

const Types = {
  item_sync: 'item_sync',
  order_sync: 'order_sync',
  order_delay: 'order_delay',
  fbm_inventory_sync: 'fbm_inventory_sync',
  fbm_order_sync: 'fbm_order_sync',
  fbm_catalog_sync: 'fbm_catalog_sync',
  flattened_sync: 'flattened_sync',
  order_risk: 'order_risk',
  notify_customer: 'notify_customer',
  multi_location: 'multi_location',
  seed_toggle: 'seed_toggle',
  inventory_sync: 'inventory_sync',
  bridge: 'bridge',
  awd: 'awd',
};

export function MarketBar({
  item: d,
  isUser,
  updateCredentials,
  doProgressSync = true,
  disconnectMarket,
  reconnect,
  refreshMetadata,
}) {
  const { disabled, hidden } = getTabPermission({
    tab: PermissionTabKey.CatalogMarketplaces,
  });

  const [loading, setLoading] = useState(false);
  const { isAdmin, isOwner } = useAuth();
  const [isDisconnectConfirmation, setDisconnectConfirmation] = useState(false);
  const [toggleOpen, setToggleOpen] = useState(null);
  const [openSellerCloudMetadata, setOpenSellerCloudMetadata] = useState(false);

  const isSeed = !!d.sync_settings.is_seed;
  const disconnectAccountLabel =
    d.shopName || d.marketplace_special_data?.shopifyStore || d.alias;

  const confirmTitle = useMemo(() => {
    switch (toggleOpen) {
      // @ts-expect-error - TS2678: Type 'string' is not comparable to type 'null'.
      case Types.item_sync:
        return `Are you sure you want to turn ${
          d.sync_settings?.item_sync ? 'off' : 'on'
        } item sync?`;

      // @ts-expect-error - TS2678: Type 'string' is not comparable to type 'null'.
      case Types.order_sync:
        return `Are you sure you want to turn ${
          d.sync_settings?.order_sync ? 'off' : 'on'
        } order sync?`;

      // @ts-expect-error - TS2678: Type 'string' is not comparable to type 'null'.
      case Types.notify_customer:
        return `Are you sure you want to turn ${
          d.sync_settings?.notify_customer ? 'off' : 'on'
        } notifying customer?`;

      // @ts-expect-error - TS2678: Type 'string' is not comparable to type 'null'.
      case Types.fbm_inventory_sync:
        return `Are you sure you want to turn ${
          d.sync_settings?.fbm_inventory_sync ? 'off' : 'on'
        } FBM Inventory sync?`;

      // @ts-expect-error - TS2678: Type 'string' is not comparable to type 'null'.
      case Types.seed_toggle:
        return d.is_seed
          ? 'Are you sure you want to remove the seed designation from this marketplace?'
          : 'Are you sure you want to make this marketplace a seed marketplace';

      // @ts-expect-error - TS2678: Type 'string' is not comparable to type 'null'.
      case Types.fbm_order_sync:
        return `Are you sure you want to turn ${
          d.sync_settings?.fbm_order_sync ? 'off' : 'on'
        } FBM Order sync?`;

      // @ts-expect-error - TS2678: Type 'string' is not comparable to type 'null'.
      case Types.fbm_catalog_sync:
        return `Are you sure you want to turn ${
          d.sync_settings?.fbm_catalog_sync ? 'off' : 'on'
        } FBM Catalog sync?`;

      // @ts-expect-error - TS2678: Type 'string' is not comparable to type 'null'.
      case Types.inventory_sync:
        return `Are you sure you want to turn ${
          d.sync_settings?.inventory_sync ? 'off' : 'on'
        } inventory sync?`;

      // @ts-expect-error - TS2678: Type 'string' is not comparable to type 'null'.
      case Types.bridge:
        return `Are you sure you want to turn ${
          d?.is_bridged ? 'off' : 'on'
        } bridge sync?`;

      // @ts-expect-error - TS2678: Type 'string' is not comparable to type 'null'.
      case Types.flattened_sync:
        return `Are you sure you want to turn ${
          d?.sync_settings?.flattened_sync ? 'off' : 'on'
        } flattened sync?`;

      // @ts-expect-error - TS2678: Type 'string' is not comparable to type 'null'.
      case Types.order_risk:
        return `Are you sure you want to turn ${
          d?.sync_settings?.order_risk ? 'off' : 'on'
        } order risk?`;

      // @ts-expect-error - TS2678: Type 'string' is not comparable to type 'null'.
      case Types.multi_location:
        return `Are you sure you want to turn ${
          d?.multi_location ? 'off' : 'on'
        } Multi Location?`;

      // @ts-expect-error - TS2678: Type 'string' is not comparable to type 'null'.
      case Types.awd:
        return `Are you sure you want to turn ${
          d?.sync_settings?.awd ? 'off' : 'on'
        } AWD sync?`;

      // @ts-expect-error - TS2678: Type 'string' is not comparable to type 'null'.
      case Types.order_delay:
        return `Are you sure you want to turn ${
          d.sync_settings?.order_delay ? 'off' : 'on'
        } order delay?`;

      default:
        return '';
    }
  }, [toggleOpen]);

  const toggleDisconnectConfirmation = () =>
    setDisconnectConfirmation(!isDisconnectConfirmation);

  const performToggle = async () => {
    if (toggleOpen === Types.order_delay) {
      setLoading(true);
      try {
        const payload = {
          orderDelay: !d.sync_settings?.order_delay,
          marketplaceId: d._id,
        };
        await MarketPlaceService.updateOrderDelay(payload);

        ToastMessage.success(
          `Order delay has been turned ${payload ? 'on' : 'off'} successfully`,
        );
        refreshMetadata();
      } catch (error) {
        ToastMessage.apiError(error);
      } finally {
        setLoading(false);
        setToggleOpen(null);
      }
      return;
    }

    if (toggleOpen === Types.seed_toggle) {
      toggleSeed();
      return;
    }
    setLoading(true);
    if (toggleOpen !== Types.bridge) {
      const updatedSyncSettings =
        toggleOpen === Types.multi_location
          ? {
              ...d.sync_settings,
              [toggleOpen]: !(d || {})[toggleOpen],
            }
          : {
              ...d.sync_settings,
              /* @ts-expect-error - TS2464: A computed property name must be of type 'string', 'number',... */
              [toggleOpen]: !(d.sync_settings || {})[toggleOpen],
            };
      setToggleOpen(null);
      await updateCredentials(
        d,
        toggleOpen,
        toggleOpen === Types.multi_location
          ? !(d || {})[toggleOpen]
          : /* @ts-expect-error - TS2538: Type 'null' cannot be used as an index type. */
            !(d.sync_settings || {})[toggleOpen],
        updatedSyncSettings,
      );
    } else {
      setToggleOpen(null);
    }
    setLoading(false);
  };

  const {
    isLoading,
    data,
    refreshSidebar: refresh,
  } = useMarketplaceSyncHook(d._id, doProgressSync);

  const progress = useMemo(() => {
    if (!doProgressSync) return 100;
    const { percentage } = data?.catalog || {};
    // @ts-expect-error - TS2345: Argument of type 'number | undefined' is not assignable to parameter of type 'st...
    return Number.isNaN(percentage) ? null : Number.parseInt(percentage, 10);
  }, [data?.catalog?.percentage, doProgressSync]);

  useEffect(() => {
    let timer;

    if (!isLoading && progress !== null && progress < 100) {
      timer = setTimeout(() => {
        refresh();
      }, 20000);
    }

    return () => {
      if (timer) {
        clearTimeout(timer);
      }
    };
  }, [isLoading, progress]);

  const isAmazon = d?.type === marketplaceNames.amazon;
  const isTiktok = d?.type === marketplaceNames.tiktok;
  const isShopify = d?.type === marketplaceNames.shopify;
  const isUSorCA = ['US', 'CA'].includes(d?.country_code);
  const isNorthAmerica = ['North America'].includes(d?.continent?.name);

  const allIntegrated = useMemo(() => {
    let all = true;
    if (d.connection_status) {
      Object.values(d.connection_status).forEach(val => {
        if (val === 0 || !val) {
          all = false;
        }
      });
    }
    return all;
  }, [d]);

  const inventorySyncAllowed = isInventorySyncAllowedMarketplace(d.type);

  const actionsAllowed = useMemo(() => isAdmin || isOwner, [isAdmin, isOwner]);

  const getItemDescription = () => {
    if (isAmazon) {
      return tooltipTexts.amazonNotApplicable;
    }

    if (isTiktok) {
      return tooltipTexts.tiktokItemSync;
    }

    const endText = `through ${brand.name} as a bridge.`;

    return `${tooltipTexts.itemSync} ${d.type} ${endText}`;
  };

  const marketplaceErrors = useMemo(() => {
    const errors: MarketplaceErrorEntry[] | undefined = d.marketplace_error;
    return CommonUtility.isValidArray(errors)
      ? errors.filter(error => error?.resolved === false)
      : [];
  }, [d?.marketplace_error]);

  const marketplaceDisconnect = () => {
    disconnectMarket(d);
  };

  const hasOrderDelaySetting =
    d?.type?.toLowerCase() === 'shopify' &&
    d?.marketplaces_details?.some(
      detail => detail?.sync_settings?.order_sync !== undefined,
    );

  const isDisabled = !!isUser || disabled;

  const resolvedLogo =
    d?.logo_uri ||
    marketplaceWordmarkImages?.[d?.type] ||
    `/images/marketplaces/${(d?.type || '').toLowerCase()}.svg`;

  const toggleSeed = async () => {
    setLoading(true);
    try {
      const payload = {
        seed: !d.sync_settings.is_seed,
        marketplace_id: d._id,
      };
      await MarketPlaceService.updateSeed(payload);

      ToastMessage.success(
        'Seed marketplace updated for the region successfully',
      );
      refreshMetadata();
    } catch (error) {
      ToastMessage.apiError(error);
    } finally {
      setLoading(false);
    }
  };

  const canBeSeed = [
    marketplaceNames.amazon,
    marketplaceNames.shopify,
    marketplaceNames.skupreme,
  ].includes(d.type);

  const hasSellerCloudWarehouseId = !!(
    d?.marketplace_metadata?.warehouseId ||
    d?.marketplace_special_data?.warehouseId
  );

  const permission = { disabled, hidden };

  return (
    <>
      <AppTableRow>
        <AppTableCell>
          <div className="sk:flex sk:items-center sk:gap-3 sk:min-[1250px]:min-w-[250px]">
            {(d.syncData || []).length > 0 ? (
              <Tooltip
                placement="top"
                content={
                  <StatusBox items={d.syncData || []} marketplace={d.type} />
                }
              >
                <span
                  role="img"
                  aria-label={
                    allIntegrated ? 'Fully connected' : 'Connection incomplete'
                  }
                  className={cn(
                    'sk:size-2.5 sk:shrink-0 sk:cursor-pointer sk:rounded-full',
                    allIntegrated ? 'sk:bg-success' : 'sk:bg-danger',
                  )}
                />
              </Tooltip>
            ) : (
              <span
                aria-hidden
                className="sk:size-2.5 sk:shrink-0 sk:rounded-full sk:bg-success"
              />
            )}
            <KitImage
              src={resolvedLogo}
              alt={d.type || ''}
              size="sm"
              fit="contain"
              loading="eager"
              placeholder={
                <span className="sk:font-semibold sk:text-fg">
                  {d.type ? d.type[0] : ''}
                </span>
              }
            />
            <div className="sk:flex sk:min-w-0 sk:flex-col">
              <span className="sk:text-sm sk:font-semibold sk:text-fg">
                {d.type}
              </span>
              {d.shopName && (
                <span className="sk:truncate sk:text-dense sk:text-fg-secondary">
                  {d.shopName}
                </span>
              )}
            </div>
            {isSeed && (
              <Badge tone="success" className="sk:shrink-0">
                Seed
              </Badge>
            )}
          </div>
        </AppTableCell>
        <AppTableCell textAlign="center">
          {CommonUtility.isValidArray(marketplaceErrors) ? (
            <MarketplaceError errors={marketplaceErrors} marketplace={d.type} />
          ) : (
            progress !== null && <PercentageProgress progress={progress} />
          )}
        </AppTableCell>
        {loading ? (
          <>
            <AppTableCell textAlign="center">
              <Spinner size="sm" label="Updating marketplace" />
            </AppTableCell>
            <AppTableCell />
            <AppTableCell />
            <AppTableCell />
          </>
        ) : (
          <>
            <AppTableCell textAlign="center">
              {canBeSeed && (
                <SyncToggle
                  {...permission}
                  className="sk:justify-center"
                  label="Seed"
                  helpText={tooltipTexts.seedMarketPlace}
                  checked={!!d.sync_settings.is_seed}
                  onToggle={() => {
                    // @ts-expect-error - TS2345: Argument of type 'string' is not assignable to parameter of type 'SetStateAction...
                    setToggleOpen(Types.seed_toggle);
                  }}
                />
              )}
            </AppTableCell>
            <AppTableCell textAlign="center">
              <SyncToggle
                {...permission}
                className="sk:justify-center"
                label="Item"
                helpText={getItemDescription()}
                checked={!!d.sync_settings?.item_sync}
                isDisabled={isDisabled || isAmazon}
                onToggle={() => {
                  if (
                    !(isDisabled || isAmazon) &&
                    (d.marketplace_id || []).length === 1
                  ) {
                    // @ts-expect-error - TS2345: Argument of type 'string' is not assignable to parameter of type 'SetStateAction...
                    setToggleOpen(Types.item_sync);
                  }
                }}
              />
            </AppTableCell>
            <AppTableCell textAlign="center">
              <SyncToggle
                {...permission}
                className="sk:justify-center"
                label="Order"
                helpText={
                  isAmazon
                    ? tooltipTexts.amazonNotApplicable
                    : tooltipTexts.orderSync
                }
                checked={!!d.sync_settings?.order_sync}
                isDisabled={isDisabled || isAmazon}
                onToggle={() => {
                  if (
                    !(isDisabled || isAmazon) &&
                    (d.marketplace_id || []).length === 1
                  ) {
                    // @ts-expect-error - TS2345: Argument of type 'string' is not assignable to parameter of type 'SetStateAction...
                    setToggleOpen(Types.order_sync);
                  }
                }}
              />
            </AppTableCell>
            <AppTableCell>
              <div className="sk:inline-flex sk:flex-col sk:gap-2">
                {isAmazon && isUSorCA && (
                  <>
                    <SyncToggle
                      {...permission}
                      label="FBM Inventory"
                      helpText={tooltipTexts.fbmInventorySync}
                      checked={!!d.sync_settings?.fbm_inventory_sync}
                      isDisabled={isDisabled}
                      onToggle={() => {
                        if (!isDisabled) {
                          // @ts-expect-error - TS2345: Argument of type 'string' is not assignable to parameter of type 'SetStateAction...
                          setToggleOpen(Types.fbm_inventory_sync);
                        }
                      }}
                    />
                    <SyncToggle
                      {...permission}
                      label="FBM Catalog"
                      helpText={tooltipTexts.fbmCatalogSync}
                      checked={!!d.sync_settings?.fbm_catalog_sync}
                      isDisabled={isDisabled}
                      onToggle={() => {
                        if (!isDisabled) {
                          // @ts-expect-error - TS2345: Argument of type 'string' is not assignable to parameter of type 'SetStateAction...
                          setToggleOpen(Types.fbm_catalog_sync);
                        }
                      }}
                    />
                    <SyncToggle
                      {...permission}
                      label="FBM Order"
                      helpText={tooltipTexts.fbmOrderSync}
                      checked={!!d.sync_settings?.fbm_order_sync}
                      isDisabled={isDisabled}
                      onToggle={() => {
                        if (!isDisabled) {
                          // @ts-expect-error - TS2345: Argument of type 'string' is not assignable to parameter of type 'SetStateAction...
                          setToggleOpen(Types.fbm_order_sync);
                        }
                      }}
                    />
                  </>
                )}
                {isAmazon && isNorthAmerica && (
                  <SyncToggle
                    {...permission}
                    label="AWD"
                    helpText={tooltipTexts.awdSync}
                    checked={!!d.sync_settings?.awd}
                    isDisabled={!!isUser || disabled}
                    onToggle={() => {
                      if (!(!!isUser || disabled)) {
                        // @ts-expect-error - TS2345: Argument of type 'string' is not assignable to parameter of type 'SetStateAction...
                        setToggleOpen(Types.awd);
                      }
                    }}
                  />
                )}
                {isTiktok && (
                  <SyncToggle
                    {...permission}
                    label="Flattened Sync"
                    helpText={tooltipTexts.flattenedSync}
                    checked={!!d.sync_settings?.flattened_sync}
                    isDisabled={isDisabled}
                    onToggle={() => {
                      if (!isDisabled) {
                        // @ts-expect-error - TS2345: Argument of type 'string' is not assignable to parameter of type 'SetStateAction...
                        setToggleOpen(Types.flattened_sync);
                      }
                    }}
                  />
                )}
                {isShopify && (
                  <>
                    <SyncToggle
                      {...permission}
                      label="Order Risk"
                      helpText={tooltipTexts.order_risk}
                      checked={!!d.sync_settings?.order_risk}
                      isDisabled={isDisabled}
                      onToggle={() => {
                        if (!isDisabled) {
                          // @ts-expect-error - TS2345: Argument of type 'string' is not assignable to parameter of type 'SetStateAction...
                          setToggleOpen(Types.order_risk);
                        }
                      }}
                    />
                    <SyncToggle
                      {...permission}
                      label="Notify Customer"
                      helpText={tooltipTexts.notify_customer}
                      checked={!!d.sync_settings?.notify_customer}
                      isDisabled={isDisabled}
                      onToggle={() => {
                        if (!isDisabled) {
                          // @ts-expect-error - TS2345: Argument of type 'string' is not assignable to parameter of type 'SetStateAction...
                          setToggleOpen(Types.notify_customer);
                        }
                      }}
                    />
                  </>
                )}

                {inventorySyncAllowed && (
                  <SyncToggle
                    {...permission}
                    label="Inventory"
                    helpText={`This configuring is for syncing inventory levels on ${d.type}. This is independent of the Item Sync, and is specific to inventory`}
                    checked={!!d.sync_settings?.inventory_sync}
                    isDisabled={isDisabled}
                    onToggle={() => {
                      if (
                        !isDisabled &&
                        (d.marketplace_id || []).length === 1
                      ) {
                        // @ts-expect-error - TS2345: Argument of type 'string' is not assignable to parameter of type 'SetStateAction...
                        setToggleOpen(Types.inventory_sync);
                      }
                    }}
                  />
                )}

                {String(d?.type || '')
                  .toLowerCase()
                  .includes('sellercloud') && (
                  <SyncToggle
                    {...permission}
                    label="Metadata"
                    helpText="Configure SellerCloud metadata such as warehouseId (required for inventory sync)."
                    checked={hasSellerCloudWarehouseId}
                    isDisabled={isDisabled}
                    onToggle={() => {
                      if (isDisabled) return;

                      if (hasSellerCloudWarehouseId) {
                        ToastMessage.warning(
                          'Disconnect the marketplace to reconfigure the Warehouse ID',
                        );
                        return;
                      }
                      setOpenSellerCloudMetadata(true);
                    }}
                  />
                )}

                {isShopify && (
                  <>
                    <SyncToggle
                      {...permission}
                      label="Multi Location"
                      helpText={tooltipTexts.multi_location}
                      checked={!!d?.multi_location}
                      isDisabled={isDisabled}
                      onToggle={() => {
                        if (!isDisabled) {
                          // @ts-expect-error - TS2345: Argument of type 'string' is not assignable to parameter of type 'SetStateAction...
                          setToggleOpen(Types.multi_location);
                        }
                      }}
                    />
                    {hasOrderDelaySetting && (
                      <SyncToggle
                        {...permission}
                        label="Order Delay"
                        helpText={tooltipTexts.order_delay}
                        checked={!!d?.sync_settings?.order_delay}
                        isDisabled={isDisabled}
                        onToggle={() => {
                          if (!isDisabled) {
                            // @ts-expect-error - TS2345: Argument of type 'string' is not assignable to parameter of type 'SetStateAction...
                            setToggleOpen(Types.order_delay);
                          }
                        }}
                      />
                    )}
                  </>
                )}
              </div>
            </AppTableCell>
            <AppTableCell>
              {actionsAllowed && (
                <div className="sk:flex sk:items-center sk:justify-end sk:gap-1">
                  {isSeed && allIntegrated && (
                    <RowAction
                      label="Re-authentication"
                      icon={<ArrowCounterClockwise size={16} aria-hidden />}
                      isDisabled={isUser}
                      onPress={() => {
                        if (!isUser) {
                          reconnect(d);
                        }
                      }}
                    />
                  )}
                  {!allIntegrated && (
                    <RowAction
                      label="Reconnect Marketplace"
                      icon={<ArrowCounterClockwise size={16} aria-hidden />}
                      isDisabled={isUser}
                      onPress={() => {
                        if (!isUser) {
                          reconnect(d);
                        }
                      }}
                    />
                  )}
                  {typeof disconnectMarket === 'function' && !hidden && (
                    <RowAction
                      label="Disconnect Marketplace"
                      icon={<X size={16} aria-hidden />}
                      isDisabled={isUser || disabled}
                      onPress={() => {
                        if (!isUser && !disabled) {
                          toggleDisconnectConfirmation();
                        }
                      }}
                    />
                  )}
                </div>
              )}
            </AppTableCell>
          </>
        )}
        <DeleteConfirmPopup
          content={
            <>
              <div className="sk:mb-3 sk:flex sk:flex-wrap sk:items-center">
                <span>
                  Please confirm disconnecting {disconnectAccountLabel} from{' '}
                  {d.type}
                </span>
                {d.country_code && (
                  <span className="sk:inline-flex sk:items-center sk:gap-1">
                    <span>&nbsp;{d.country_code}</span>
                    <Flag name={d.country_code} />
                  </span>
                )}
              </div>
              <div>
                Only this credential is removed. Existing listings, catalog
                records, mappings, and shared configuration are preserved for
                reconciliation. Other connected accounts are not affected.
              </div>
            </>
          }
          noButtonTitle="Cancel"
          onClose={toggleDisconnectConfirmation}
          onConfirm={marketplaceDisconnect}
          open={isDisconnectConfirmation}
          size="tiny"
          title="Are you sure want to disconnect?"
        />
      </AppTableRow>
      <DeleteConfirmPopup
        size="tiny"
        open={!!toggleOpen}
        onClose={() => setToggleOpen(null)}
        onConfirm={() => performToggle()}
        title={confirmTitle}
        noButtonTitle="Cancel"
        content={
          toggleOpen === Types.seed_toggle && (
            <div>
              <p>
                <b>Turning off Seed</b> will not affect your catalog by itself.
                However, if <b>Item Sync</b> is enabled, disabling Seed means
                {brand.name} will overwrite and push catalog changes into
                Shopify. Please use caution when adjusting these settings.
              </p>
              <p>
                If you prefer to keep Shopify as the source of truth for catalog
                details, you can leave <b>Inventory Sync</b> enabled. This will
                allow {brand.name} to passively manage inventory levels and
                orders while leaving full catalog control within Shopify.
              </p>
            </div>
          )
        }
      />
      <SellerCloudMetadataPopup
        open={openSellerCloudMetadata}
        onClose={() => setOpenSellerCloudMetadata(false)}
        marketplaceId={d.marketplace_id && d.marketplace_id[0]}
        initialWarehouseId={
          d?.marketplace_metadata?.warehouseId ||
          d?.marketplace_special_data?.warehouseId ||
          ''
        }
        refetch={refreshMetadata}
      />
    </>
  );
}

export function AdsBar({ data }) {
  return (
    <div className="sk:flex sk:items-center sk:gap-2 sk:rounded-card sk:bg-surface sk:px-5 sk:py-3 sk:shadow-card sk:inset-shadow-hairline">
      <img src={data?.imgSrcActive} alt="" className="sk:h-8 sk:w-auto" />
      {CommonUtility.isValidArray(data?.countries_code) &&
        data?.countries_code.map(c => <Flag key={c} name={c} />)}
      {!CommonUtility.isValidArray(data?.countries_code) &&
        data?.country_code && <Flag name={data.country_code} />}
    </div>
  );
}
